/**
 * TEXT CONSENT — the ONE SMS opt-out rule, shared by every send surface.
 *
 * WHY THIS EXISTS
 *   "Can this patient be texted?" was answered inconsistently across the app —
 *   some surfaces checked TxtMsgOk, some checked nothing, and the new
 *   texted-STOP ledger (SmsOptOutAction) wasn't checked anywhere. This module is
 *   the single source of truth: a patient is blocked from texts when ANY of
 *     - they texted STOP/CANCEL etc.   (SmsOptOutAction === 'opt_out')
 *     - their OD record says no texts   (TxtMsgOk === 2)
 *     - the server says blocked         (the /sms-opt-out-status endpoint result,
 *                                        or an HTTP-200 send refusal)
 *   The frontend cannot see the HQ commoptout list directly — that only arrives
 *   through the endpoint or a server refusal, which is why `optOutResult` can
 *   override the local patient-data checks.
 *
 * MESSAGE PRECEDENCE (matches the backend, deliberately)
 *   1. a blocked endpoint/server result — its code + reason are shown verbatim
 *   2. texted opt-out                    — "opted out by replying STOP on …"
 *   3. TxtMsgOk === 2                     — "Open Dental record says no texts"
 *
 * COPY IS SHARED WITH THE BACKEND
 *   The rendered strings MUST match the backend byte for byte — they are the
 *   same product copy on two sides. TEXT_BLOCK_MESSAGES holds the two static
 *   copy strings so both sides can anchor to one place.
 *
 * THIS FILE IS PURE
 *   No Vue, no Vuex, no localStorage, no imports — exactly like
 *   src/utils/phiMask.js. That is what lets
 *   cypress/node-tests/text-consent-helper.test.js load and EXECUTE this file
 *   directly (stripping `export`) instead of mirroring it, so the tests bind to
 *   shipped code.
 */

/* -- Block codes (identical to the endpoint's `code` field) ----------------- */
export const TEXT_BLOCK_CODES = {
  OPTED_OUT_BY_TEXT: 'opted_out_by_text',
  OD_TEXT_NOT_OK: 'od_text_not_ok',
  OD_OPTED_OUT: 'od_opted_out',
};

/* -- The single default code for a blocked result with no usable code --------
 * Both entry points (getTextBlockReason's endpoint-result branch and
 * optOutResultFromSendFailure's send-refusal branch) MUST agree on this — they
 * feed the SAME UI, so a split default is a silent inconsistency. A blocked
 * result with no code means OD/HQ is the authority for the block, so it is
 * reported as OD_OPTED_OUT. */
export const DEFAULT_BLOCK_CODE = TEXT_BLOCK_CODES.OD_OPTED_OUT;

/* -- Static copy strings (shared with the backend) --------------------------
 * OPTED_OUT_BY_TEXT is a template: {keyword} is the reply that opted them out
 * (defaults to STOP) and {on} is " on M/D/YYYY", dropped entirely when there is
 * no valid date. OD_TEXT_NOT_OK is fully static. GENERIC_OPT_OUT is the
 * last-resort copy so a blocked result never surfaces a null/empty message. */
export const TEXT_BLOCK_MESSAGES = {
  OPTED_OUT_BY_TEXT:
    'Not sent: this patient opted out of texts by replying {keyword}{on}. They can text START to opt back in.',
  OD_TEXT_NOT_OK: "Not sent: this patient's Open Dental record says no text messages.",
  GENERIC_OPT_OUT: 'Not sent: this patient has opted out of text messages.',
};

/* -- First argument that is a non-empty string once trimmed (else null) ----- */
function firstText() {
  for (let i = 0; i < arguments.length; i++) {
    const v = arguments[i];
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return null;
}

/* -- Case-insensitive field read (PascalCase or camelCase) ------------------ */
function pick(patient, canonicalKey) {
  if (!patient || typeof patient !== 'object') return undefined;
  const target = String(canonicalKey).toLowerCase();
  for (const k in patient) {
    if (Object.prototype.hasOwnProperty.call(patient, k) && String(k).toLowerCase() === target) {
      return patient[k];
    }
  }
  return undefined;
}

/* -- Parse a UTC 'YYYY-MM-DD HH:mm:ss' string (or ISO) -> Date | null -------- */
function parseUtc(value) {
  if (!value || typeof value !== 'string') return null;
  let iso = value.trim().replace(' ', 'T');
  // Append the UTC marker only when the string carries no timezone of its own.
  if (!/[zZ]$/.test(iso) && !/[+-]\d{2}:?\d{2}$/.test(iso)) iso += 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/* -- Render a Date as M/D/YYYY in `timezone`; invalid/missing tz -> UTC ------ */
function formatDateInZone(date, timezone) {
  const tz = timezone || 'UTC';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric',
    }).format(date);
  } catch (e) {
    // Invalid IANA zone -> never throw, fall back to UTC.
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', year: 'numeric', month: 'numeric', day: 'numeric',
    }).format(date);
  }
}

function buildOptOutMessage(keyword, date, timezone) {
  const kw = keyword || 'STOP';
  const on = date ? ` on ${formatDateInZone(date, timezone)}` : '';
  return TEXT_BLOCK_MESSAGES.OPTED_OUT_BY_TEXT
    .replace('{keyword}', kw)
    .replace('{on}', on);
}

/**
 * The one rule. Returns { blocked, code, message }.
 *
 * @param {Object}  args
 * @param {Object}  args.patient       patient row (Pascal- or camelCase keys)
 * @param {Object} [args.optOutResult] one result object from /sms-opt-out-status
 *                                      (or optOutResultFromSendFailure) — when
 *                                      blocked, its code + reason win outright
 * @param {string} [args.timezone]     IANA zone for the opt-out date
 */
export function getTextBlockReason(args) {
  const { patient, optOutResult, timezone } = args || {};

  // 1. A blocked server/endpoint result overrides everything. Its reason is shown
  //    verbatim when present; a blocked result must never surface a null/empty
  //    message, so a missing/blank reason falls back to the generic copy, and a
  //    missing/blank code falls back to DEFAULT_BLOCK_CODE (shared with the
  //    send-refusal path so the two never disagree).
  if (optOutResult && optOutResult.blocked === true) {
    return {
      blocked: true,
      code: firstText(optOutResult.code) || DEFAULT_BLOCK_CODE,
      message: firstText(optOutResult.reason) || TEXT_BLOCK_MESSAGES.GENERIC_OPT_OUT,
    };
  }

  // 2. Texted opt-out (STOP/CANCEL …) — wins over TxtMsgOk.
  const action = pick(patient, 'SmsOptOutAction');
  if (action === 'opt_out') {
    const keyword = pick(patient, 'SmsOptOutKeyword');
    const date = parseUtc(pick(patient, 'SmsOptOutAt'));
    return {
      blocked: true,
      code: TEXT_BLOCK_CODES.OPTED_OUT_BY_TEXT,
      message: buildOptOutMessage(keyword, date, timezone),
    };
  }

  // 3. OD record says no texts.
  if (Number(pick(patient, 'TxtMsgOk')) === 2) {
    return {
      blocked: true,
      code: TEXT_BLOCK_CODES.OD_TEXT_NOT_OK,
      message: TEXT_BLOCK_MESSAGES.OD_TEXT_NOT_OK,
    };
  }

  return { blocked: false, code: null, message: null };
}

/** Convenience boolean. */
export function isTextBlocked(args) {
  return getTextBlockReason(args).blocked === true;
}

/**
 * Adapt an HTTP-200 send refusal into an `optOutResult` for getTextBlockReason.
 * Shape refused: { success:false, error:'PATIENT_OPTED_OUT', reason, message,
 * blockCode, since }. Anything else -> null.
 */
export function optOutResultFromSendFailure(responseData) {
  if (
    !responseData ||
    typeof responseData !== 'object' ||
    responseData.success !== false ||
    responseData.error !== 'PATIENT_OPTED_OUT'
  ) {
    return null;
  }
  return {
    blocked: true,
    code: firstText(responseData.blockCode) || DEFAULT_BLOCK_CODE,
    reason: firstText(responseData.reason, responseData.message) || TEXT_BLOCK_MESSAGES.GENERIC_OPT_OUT,
  };
}
